import { useContext, useEffect, useState } from "react";
// import CloseIcon from "@mui/icons-material/Close";
// import CreateIcon from "@mui/icons-material/Create";
import { IconSquareRoundedPlus } from "@tabler/icons-react";
import { Button, IconButton } from "@mui/material";
import { HistoryItemsHeader, HistoryItemsContainer, SectionHeader } from "../chat.styled";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  setActiveModel,
  setAiMessageStatus,
  setConversations,
  setCurrentConversation,
} from "modules/chat/store/conversationList";
import ConversationMenuItem from "./historyItem";
import { setHeader } from "core/store/shared/header";
import { ConversationType, MessageType } from "../chat.type";
import MenuClose from "assets/MenuClose";
import { AuthContext } from "core/contexts/auth/auth";
import { getConversationsFromApi } from "modules/chat/services/chatService";
import { RefreshIcon } from "assets/RefreshIcon";
import { ArrowDown } from "assets/ArrowDown";
import { ArrowUp } from "assets/ArrowUp";
import HistoryIcon from "assets/HistoryIcon";
import MenuOpen from "assets/MenuOpen";
import { useAppTheme } from "core/contexts/theme/theme";

const formatDate = (date) => new Date(date);

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date) => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date) => {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return date > oneMonthAgo && !isPastWeek(date);
};

const getConversationDate = (conversation: ConversationType): Date => {
  if (!conversation.updatedAt) {
    // If updatedAt is undefined, use a fallback date (e.g., epoch or current time)
    return new Date(0);
  }

  // Otherwise, directly convert to Date
  return new Date(conversation.updatedAt);
};

const groupConversationByDate = (conversations: ConversationType[]) => {
  const todayConversation: ConversationType[] = [];
  const yesterdayConversation: ConversationType[] = [];
  const pastWeekConversation: ConversationType[] = [];
  const lastMonthConversation: ConversationType[] = [];
  const olderConversation: ConversationType[] = [];

  // First group the conversations
  conversations.forEach((conversation: ConversationType) => {
    const conversationDate = formatDate(conversation.updatedAt);

    if (isToday(conversationDate)) {
      todayConversation.push(conversation);
    } else if (isYesterday(conversationDate)) {
      yesterdayConversation.push(conversation);
    } else if (isPastWeek(conversationDate)) {
      pastWeekConversation.push(conversation);
    } else if (isPastMonth(conversationDate)) {
      lastMonthConversation.push(conversation);
    } else {
      olderConversation.push(conversation);
    }
  });

  // Then sort each category by updatedAt (newest first)
  const sortByCreatedAt = (a: ConversationType, b: ConversationType) => {
    const dateA = getConversationDate(a).getTime();
    const dateB = getConversationDate(b).getTime();
    return dateB - dateA;
  };

  todayConversation.sort(sortByCreatedAt);
  yesterdayConversation.sort(sortByCreatedAt);
  pastWeekConversation.sort(sortByCreatedAt);
  lastMonthConversation.sort(sortByCreatedAt);
  olderConversation.sort(sortByCreatedAt);

  return {
    todayConversation,
    yesterdayConversation,
    pastWeekConversation,
    lastMonthConversation,
    olderConversation,
  };
};

export default function ChatHistory({
  isMenuOpen,
  handleOpenMenu,
  handleStartNewConversation,
  socket,
  setConversationFromHistory,
  setShowCompleteHistory,
  showCompleteHistory,
}) {
  // Redux States
  const convoState = useAppSelector((state) => state.chatConversations);
  const dispatch = useAppDispatch();
  const { checkAuthToken } = useContext(AuthContext);
  const [highlightedConversation, setHighlightedConversation] = useState("");
  const [loading, setIsLoading] = useState<boolean>(false);
  const [todayConversation, setTodayConversation] = useState([]);
  const [yesterdayConversation, setYesterdayConversation] = useState([]);
  const [pastWeekConversation, setPastWeekConversation] = useState([]);
  const [lastMonthConversation, setLastMonthConversation] = useState([]);
  const [olderConversation, setOlderConversation] = useState([]);
  const [hidden, setHidden] = useState<{ [id: string]: boolean }>({
    day: false,
    yesterday: true,
    week: true,
    month: true,
    older: true,
  });

  function sortMessagesByGroupId(conversation) {
    // Step 1: Group the user and assistant messages by chatid
    const userMessages = conversation.messages.filter(
      (msg) => msg.role === "user"
    );
    const assistantMessages = conversation.messages.filter(
      (msg) => msg.role === "assistant"
    );

    // Step 2: Create pairs of user message and corresponding assistant message
    const pairedMessages = userMessages.map((userMsg) => {
      const correspondingAssistant = assistantMessages.find(
        (assistantMsg) => assistantMsg.chatid === userMsg.chatid
      );
      return { userMsg, assistantMsg: correspondingAssistant };
    });

    // Step 3: Sort the pairs by user message's createdAt
    pairedMessages.sort(
      (a, b) =>
        Date.parse(a.userMsg.createdAt) - Date.parse(b.userMsg.createdAt)
    );

    // Step 4: Flatten the sorted pairs back into the original order (user followed by assistant)
    const sortedMessages = pairedMessages.flatMap((pair) => [
      pair.userMsg,
      pair.assistantMsg,
    ]);

    return {
      ...conversation,
      messages: sortedMessages,
    };
  }

  function remapAndUpdateConversations(response) {
    const conversationMap = response.reduce((acc, conversation) => {
      // Set the ID and add the convo as its value
      acc[conversation.conversationId] = sortMessagesByGroupId(conversation);
      return acc;
    }, {} as Record<string, ConversationType>);

    // Update the conversations map in Redux: note, only 1 message is expected to be emitted back to the client
    dispatch(setConversations({ conversations: conversationMap }));

    const {
      todayConversation,
      yesterdayConversation,
      pastWeekConversation,
      lastMonthConversation,
      olderConversation,
    }: any = groupConversationByDate(response);

    setTodayConversation(todayConversation);
    setYesterdayConversation(yesterdayConversation);
    setPastWeekConversation(pastWeekConversation);
    setLastMonthConversation(lastMonthConversation);
    setOlderConversation(olderConversation);

    // Turn the loading animation off
    setIsLoading(false);
  }

  useEffect(() => {
    if (socket) {
      // The endpoint returns a dict of 'conversations' and 'totalCount'
      const onConversations = (response: {
        conversations: ConversationType[];
        totalCount: number;
      }) => {
        try {
          // Re-format the list into our dictionary { ID: conversation } format, used by this app
          remapAndUpdateConversations(response.conversations);
        } catch (error) {
          console.error("Error setting conversations:", error);
        }
      };

      // Create the listener, it calls the 'onConversations'
      socket.on("conversations_list", onConversations);

      // Send the message to the backend to retrieve the user's conversations
      socket.emit("list_conversations");

      return () => {
        socket.off("conversations_list");
      };
    }
  }, [socket]);

  /**
   * Refresh the conversations when you create a new conversaton
   */
  useEffect(() => {
    if (convoState && Object.keys(convoState.history).length >= 0) {
      const {
        todayConversation,
        yesterdayConversation,
        pastWeekConversation,
        lastMonthConversation,
        olderConversation,
      }: any = groupConversationByDate(Object.values(convoState.history));

      setTodayConversation(todayConversation);
      setYesterdayConversation(yesterdayConversation);
      setPastWeekConversation(pastWeekConversation);
      setLastMonthConversation(lastMonthConversation);
      setOlderConversation(olderConversation);

      // Turn the loading animation off
      setIsLoading(false);
    }
  }, [convoState.history]);
  
  /**
   * Update the highlighted conversation when the state changes for the current state (e.g. change via other component)
   */
  useEffect(() => {
    if (convoState.current) {
      setHighlightedConversation(convoState.current)
    }
  }, [convoState.current]);

  /**
   * As the Socket.io for conversations does not seem to update the list as messages change
   * here is a function with HTTP to amend this issue, until the socket backend is fixed
   */
  const refreshConversations = async () => {
    setIsLoading(true);
    const token = await checkAuthToken();
    const response = await getConversationsFromApi(token);
    remapAndUpdateConversations(response);
  };

  /**
   * Toggle the history expand/contract
   */
  const handleSetHidden = (category, toggle) => {
    setHidden((prevState) => ({
      ...prevState,
      [category]: toggle,
    }));
  };

  const {apptheme} = useAppTheme();
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <HistoryItemsHeader className="p-1 d-flex flex-column justify-content-start">
        <span className="d-flex flex-row p-2 mb-3 mt-1 cursor-pointer" onClick={() => handleOpenMenu(!isMenuOpen)}>
          <Button
            className={`absolute p-2`}
            style={{color: apptheme.colors.textColor}}
          >
            {isMenuOpen ? <MenuClose /> :  <MenuOpen />}
          </Button>

          <h3 className="ps-2 hide-on-short-menu">Conversations</h3>
        </span>

        <span className="d-flex flex-row p-2 cursor-pointer" onClick={() => refreshConversations()}>
          <Button
            className={`absolute p-2 rounded ${loading ? "loading" : ""}`}
            style={{color: apptheme.colors.textColor}}
          >
            <RefreshIcon />
          </Button>
          <h3 className="ps-2 hide-on-short-menu">Refresh Conversations</h3>
        </span>

        <span className="d-flex flex-row p-2 cursor-pointer" onClick={() => {
          setShowCompleteHistory(false)
          handleStartNewConversation()
        }}>
          <Button
            className={`absolute p-2`}
            style={{color: apptheme.colors.textColor}}
          >
            <IconSquareRoundedPlus />
          </Button>
          <h3 className="ps-2 hide-on-short-menu">New Conversation</h3>
        </span>

        <span className="d-flex flex-row p-2 cursor-pointer" onClick={() => setShowCompleteHistory(true)}>
          <Button
            className={`absolute p-2`}
            style={{color: apptheme.colors.textColor}}
          >
            <HistoryIcon />
          </Button>
          <h3 className="ps-2 hide-on-short-menu">Full History</h3>
        </span>
      </HistoryItemsHeader>
      <div className="px-1 hide-on-short-menu" style={{ overflowY: 'auto', position: 'relative', flex: 1, minHeight: 0 }}>
        <div className="d-flex flex-column cursor-pointer w-100 py-2">
          <HistoryItemsContainer className="d-flex flex-column">
            <SectionHeader
              className="d-flex flex-row justify-content-between mb-1"
              onClick={() => handleSetHidden("day", !hidden["day"])}
            >
              <h3 className="user-select-none">Today</h3>
              {hidden["day"] ? (
                <div>
                  <ArrowDown />
                </div>
              ) : (
                <div>
                  <ArrowUp />
                </div>
              )}
            </SectionHeader>
            {hidden["day"] === false && (
              <>
                {todayConversation.map((conversation: ConversationType) => {
                  return (
                    <ConversationMenuItem
                      key={`history-${conversation.conversationId}`}
                      conversation={conversation}
                      setConversationFromHistory={setConversationFromHistory}
                      handleOpenMenu={handleOpenMenu}
                      convoState={convoState}
                      highlightedConversation={highlightedConversation}
                      setHighlightedConversation={setHighlightedConversation}
                    />
                  );
                })}
              </>
            )}
          </HistoryItemsContainer>

          <HistoryItemsContainer className="d-flex flex-column">
            <SectionHeader
              className="d-flex flex-row justify-content-between mb-1"
              onClick={() => handleSetHidden("yesterday", !hidden["yesterday"])}
            >
              <h3 className="user-select-none">Yesterday</h3>
              {hidden["yesterday"] ? (
                <div>
                  <ArrowDown />
                </div>
              ) : (
                <div>
                  <ArrowUp />
                </div>
              )}
            </SectionHeader>
            {hidden["yesterday"] === false && (
              <>
                {yesterdayConversation.map((conversation: ConversationType) => {
                  return (
                    <ConversationMenuItem
                      key={`history-${conversation.conversationId}`}
                      conversation={conversation}
                      setConversationFromHistory={setConversationFromHistory}
                      handleOpenMenu={handleOpenMenu}
                      convoState={convoState}
                      highlightedConversation={highlightedConversation}
                      setHighlightedConversation={setHighlightedConversation}
                    />
                  );
                })}
              </>
            )}
          </HistoryItemsContainer>

          <HistoryItemsContainer className="d-flex flex-column">
            <SectionHeader
              className="d-flex flex-row justify-content-between mb-1"
              onClick={() => handleSetHidden("week", !hidden["week"])}
            >
              <h3 className="user-select-none">Past Week</h3>
              {hidden["week"] ? (
                <div>
                  <ArrowDown />
                </div>
              ) : (
                <div>
                  <ArrowUp />
                </div>
              )}
            </SectionHeader>
            {hidden["week"] === false && (
              <>
                {pastWeekConversation.map((conversation: ConversationType) => {
                  return (
                    <ConversationMenuItem
                      key={`history-${conversation.conversationId}`}
                      conversation={conversation}
                      setConversationFromHistory={setConversationFromHistory}
                      handleOpenMenu={handleOpenMenu}
                      convoState={convoState}
                      highlightedConversation={highlightedConversation}
                      setHighlightedConversation={setHighlightedConversation}
                    />
                  );
                })}
              </>
            )}
          </HistoryItemsContainer>

          <HistoryItemsContainer className="d-flex flex-column">
            <SectionHeader
              className="d-flex flex-row justify-content-between mb-1"
              onClick={() => handleSetHidden("month", !hidden["month"])}
            >
              <h3 className="user-select-none">Last Month</h3>
              {hidden["month"] ? (
                <div>
                  <ArrowDown />
                </div>
              ) : (
                <div>
                  <ArrowUp />
                </div>
              )}
            </SectionHeader>
            {hidden["month"] === false && (
              <>
                {lastMonthConversation.map((conversation: ConversationType) => {
                  return (
                    <ConversationMenuItem
                      key={`history-${conversation.conversationId}`}
                      conversation={conversation}
                      setConversationFromHistory={setConversationFromHistory}
                      handleOpenMenu={handleOpenMenu}
                      convoState={convoState}
                      highlightedConversation={highlightedConversation}
                      setHighlightedConversation={setHighlightedConversation}
                    />
                  );
                })}
              </>
            )}
          </HistoryItemsContainer>

          <HistoryItemsContainer className="d-flex flex-column">
            <SectionHeader
              className="d-flex flex-row justify-content-between mb-1"
              onClick={() => handleSetHidden("older", !hidden["older"])}
            >
              <h3 className="user-select-none">Older</h3>
              {hidden["older"] ? (
                <div>
                  <ArrowDown />
                </div>
              ) : (
                <div>
                  <ArrowUp />
                </div>
              )}
            </SectionHeader>
            {hidden["older"] === false && (
              <>
                {olderConversation.map((conversation: ConversationType) => {
                  return (
                    <ConversationMenuItem
                      key={`history-${conversation.conversationId}`}
                      conversation={conversation}
                      setConversationFromHistory={setConversationFromHistory}
                      handleOpenMenu={handleOpenMenu}
                      convoState={convoState}
                      highlightedConversation={highlightedConversation}
                      setHighlightedConversation={setHighlightedConversation}
                    />
                  );
                })}
              </>
            )}
          </HistoryItemsContainer>
        </div>
      </div>
    </div>
  );
}
